import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Globe, ArrowRight, Phone, Mail, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { t } from "@/i18n";
import BookingForm from "@/components/BookingForm";
import BookingCalendar from "@/components/BookingCalendar";
import ReviewForm from "@/components/ReviewForm";
import ReviewsShowcase from "@/components/ReviewsShowcase";

const HERO_IMAGES = [
  "https://images.unsplash.com/photo-1599074681549-902b476320d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_exploratory-tour/artifacts/fyp7wo4l_Glorieta.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_exploratory-tour/artifacts/av13uo25_FuenteHabaneras.jpg",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_exploratory-tour/artifacts/hnn3if8j_HombreDelMar.webp",
  "https://customer-assets-wrfwihn1.emergentagent.net/job_exploratory-tour/artifacts/kuqwpud1_BellaLola2.jpg",
];

const GOOGLE_REVIEWS_URL = "https://www.google.com/search?sca_esv=edd3672440a57cf7&q=free+tour+torrevieja&si=APenkKm7iecQ4G6P-TsbSMFKIQtv3EFIqRAFw-i8uEbk55Z-_xwOQrTbJiTFhhsk8D0HfdPe4mu5CzoOwV9nJOSOPZKi7w_AQQEf38XWzyF2XPnmyN1-GPU%3D&uds=AJ5uw19ot_8pmnrWc3HjfIZM6VfDJRH2f0flWECfCekXSxfzLgwX-SJT_GTcOTeMoRsHnLvioxBDCtLUSgs3-jxD4B_9YEEZOnJ_jBXfmc67GDC4hZGp9IQ&sa=X";

const emptyForm = {
  name: "", email: "", phone: "", party_size: 1,
  topics: [], custom_topic: "", rgpd_consent: false,
};

const Booking = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(null);
  const [step, setStep] = useState("language");
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [choice, setChoice] = useState(null);

  const L = t[lang] || t.es;

  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const isNew = !localStorage.getItem("ft_visited");
    api.post("/track-visit", { new_visitor: isNew }).catch(() => {});
    if (isNew) localStorage.setItem("ft_visited", "1");
  }, []);

  const chooseLang = (l) => {
    setLang(l);
    setStep("intro");
  };

  const submitBooking = async () => {
    const { date: dateIso, slot } = choice;
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        ...form,
        party_size: parseInt(form.party_size, 10) || 1,
        language: lang,
        date: dateIso,
        slot,
      });
      setStep("pending");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "email_not_valid") {
        toast.error(L.emailInvalid);
      } else if (e.response?.status === 409) {
        toast.error(typeof detail === "string" ? detail : L.errorGeneric);
        setStep("calendar");
      } else {
        toast.error(typeof detail === "string" ? detail : L.errorGeneric);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm(emptyForm);
    setLang(null);
    setStep("language");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row relative z-10">
      {/* Left visual panel */}
      <div className="relative lg:w-[42%] h-56 lg:h-screen lg:sticky lg:top-0 overflow-hidden bg-coastal-text">
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt="Torrevieja"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
            style={{ opacity: i === heroIdx ? 1 : 0 }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-coastal-text/70 via-coastal-text/20 to-transparent" />
        <div className="absolute bottom-0 left-0 p-8 lg:p-12">
          <p className="text-white/80 text-sm tracking-[0.2em] uppercase mb-2">Torrevieja · España</p>
          <h1 className="font-heading text-white text-3xl lg:text-5xl leading-tight drop-shadow">
            {L.brand}
          </h1>
        </div>
        <div className="absolute bottom-6 right-6 flex gap-1.5">
          {HERO_IMAGES.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === heroIdx ? "w-6 bg-white" : "w-1.5 bg-white/50"}`}
            />
          ))}
        </div>
        <div className="absolute top-5 right-5 flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <button
                data-testid="contact-btn"
                className="text-white/90 hover:text-white text-xs bg-white/15 backdrop-blur-md px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5"
              >
                <Phone size={12} /> {L.contact}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-sm" data-testid="contact-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-coastal-text">{L.contactTitle}</DialogTitle>
                <DialogDescription>{L.contactDesc}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 mt-2">
                <a
                  href="mailto:torreviejafreetour@gmail.com"
                  data-testid="contact-email"
                  className="flex items-center gap-3 p-3 rounded-lg border border-coastal-secondary hover:border-coastal-primary transition-colors"
                >
                  <Mail className="text-coastal-primary" size={20} />
                  <div>
                    <p className="text-xs text-coastal-muted">Email</p>
                    <p className="text-coastal-text font-medium">torreviejafreetour@gmail.com</p>
                  </div>
                </a>
                <a
                  href="tel:+34623224305"
                  data-testid="contact-phone"
                  className="flex items-center gap-3 p-3 rounded-lg border border-coastal-secondary hover:border-coastal-primary transition-colors"
                >
                  <Phone className="text-coastal-primary" size={20} />
                  <div>
                    <p className="text-xs text-coastal-muted">{L.phone}</p>
                    <p className="text-coastal-text font-medium">+34 623 22 43 05</p>
                  </div>
                </a>
                <a
                  href={GOOGLE_REVIEWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-google"
                  className="flex items-center gap-3 p-3 rounded-lg border border-coastal-secondary hover:border-coastal-primary transition-colors"
                >
                  <Star className="text-amber-400 fill-amber-400" size={20} />
                  <span className="text-coastal-text font-medium">Reseñas de Google</span>
                </a>
              </div>
              <div className="mt-4 pt-4 border-t border-coastal-secondary">
                <ReviewForm
                  language={lang || "es"}
                  prompt={L.reviewPrompt}
                  namePlaceholder={L.reviewName}
                  placeholder={L.reviewPlaceholder}
                  sendLabel={L.reviewSend}
                  sendingLabel={L.reviewSending}
                  thanksLabel={L.reviewThanks}
                />
              </div>
            </DialogContent>
          </Dialog>
          <button
            onClick={() => navigate("/admin")}
            data-testid="admin-link"
            className="text-white/70 hover:text-white text-xs bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full transition-colors"
          >
            Admin
          </button>
        </div>
      </div>

      {/* Right content panel */}
      <div className="flex-1 flex items-center justify-center p-6 pb-44 sm:p-10 lg:p-16">
        <AnimatePresence mode="wait">
          {step === "language" && (
            <motion.div
              key="language"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="w-full max-w-md text-center"
              data-testid="language-step"
            >
              <Globe className="mx-auto text-coastal-primary mb-4" size={40} />
              <h2 className="font-heading text-4xl text-coastal-text mb-1">{t.es.langTitle}</h2>
              <p className="text-coastal-muted mb-10">{t.en.langTitle}</p>
              <div className="grid grid-cols-2 gap-4">
                <button
                  data-testid="lang-es-button"
                  onClick={() => chooseLang("es")}
                  className="group p-6 rounded-2xl border border-coastal-secondary bg-white hover:border-coastal-primary hover:-translate-y-1 transition-transform duration-200 shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
                >
                  <div className="text-4xl mb-2">🇪🇸</div>
                  <div className="font-semibold text-coastal-text">Español</div>
                </button>
                <button
                  data-testid="lang-en-button"
                  onClick={() => chooseLang("en")}
                  className="group p-6 rounded-2xl border border-coastal-secondary bg-white hover:border-coastal-primary hover:-translate-y-1 transition-transform duration-200 shadow-[0_8px_32px_rgba(0,0,0,0.06)]"
                >
                  <div className="text-4xl mb-2">🇬🇧</div>
                  <div className="font-semibold text-coastal-text">English</div>
                </button>
              </div>
              <button
                data-testid="lang-it-button"
                onClick={() => chooseLang("it")}
                className="group mt-4 w-full p-4 rounded-2xl border border-coastal-secondary bg-white hover:border-coastal-primary transition-colors duration-200 shadow-[0_8px_32px_rgba(0,0,0,0.06)] flex items-center justify-center gap-3"
              >
                <span className="text-3xl">🇮🇹</span>
                <span className="font-semibold text-coastal-text">Italiano</span>
              </button>
            </motion.div>
          )}

          {step === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              className="w-full max-w-lg"
              data-testid="intro-step"
            >
              <div className="text-4xl mb-4">{lang === "es" ? "🇪🇸" : lang === "it" ? "🇮🇹" : "🇬🇧"}</div>
              <h2 className="font-heading text-4xl text-coastal-text mb-5 leading-tight">{L.introTitle}</h2>
              <p className="text-coastal-muted text-lg leading-relaxed mb-4">{L.introText}</p>
              <p className="text-coastal-muted leading-relaxed mb-8">{L.introHelp}</p>
              <div className="rounded-2xl bg-coastal-primary/5 border-l-4 border-coastal-primary p-5 mb-8" data-testid="what-we-do">
                <h3 className="font-heading text-2xl text-coastal-text mb-2">{L.whatTitle}</h3>
                <p className="text-coastal-muted leading-relaxed">{L.whatText}</p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  data-testid="change-lang-btn"
                  onClick={() => setStep("language")}
                  className="rounded-full border-coastal-secondary text-coastal-muted"
                >
                  {L.changeLang}
                </Button>
                <Button
                  data-testid="start-booking-btn"
                  onClick={() => setStep("calendar")}
                  className="rounded-full bg-coastal-primary hover:bg-coastal-primary/90 text-white px-8 flex-1 transition-transform duration-200 hover:-translate-y-0.5"
                >
                  {L.start} <ArrowRight size={16} className="ml-1" />
                </Button>
              </div>
              <ReviewsShowcase title={L.reviewsTitle} sourceLabel={L.reviewsSource} />
            </motion.div>
          )}

          {step === "form" && (
            <BookingForm
              key="form"
              lang={lang}
              data={form}
              onChange={setForm}
              choice={choice}
              submitting={submitting}
              onNext={submitBooking}
              onBack={() => setStep("calendar")}
            />
          )}

          {step === "calendar" && (
            <BookingCalendar
              key="calendar"
              lang={lang}
              party={form.party_size}
              onPartyChange={(v) => setForm((f) => ({ ...f, party_size: v }))}
              initial={choice}
              onBack={() => setStep("intro")}
              onSubmit={(date, slot) => { setChoice({ date, slot }); setStep("form"); }}
            />
          )}

          {step === "pending" && (
            <motion.div
              key="pending"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="w-full max-w-lg text-center"
              data-testid="pending-step"
            >
              <div className="mx-auto w-16 h-16 rounded-full bg-coastal-accent flex items-center justify-center mb-6">
                <Mail className="text-white" size={30} />
              </div>
              <h2 className="font-heading text-4xl text-coastal-text mb-4">{L.pendingTitle}</h2>
              <p className="text-coastal-muted text-lg leading-relaxed mb-3">{L.pendingText(form.email)}</p>
              <div className="bg-coastal-accent/10 text-coastal-text rounded-lg p-4 text-sm mb-2 font-medium">
                {L.pendingWarn}
              </div>
              <p className="text-coastal-muted text-sm mb-8">{L.pendingSpam}</p>
              <Button
                data-testid="new-booking-btn"
                onClick={reset}
                variant="outline"
                className="rounded-full border-coastal-secondary text-coastal-text"
              >
                {L.newBooking}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Booking;
